/**
 * Login con magic link según handoff.
 *
 * Sprint 11 v2 S11-V2-04. Replica `docs/PAGINAS-TRANSVERSALES.md § 1`:
 *
 * - PublicLayout centrado (lo provee la ruta padre en App.tsx).
 * - Logo Calbucura grande arriba (escudo + texto "CALBUCURA").
 * - Sub-título "Portal de vecinos".
 * - Card central con título "Inicia sesión" + texto explicativo +
 *   input email + Button primary large fullWidth + estado de éxito.
 * - Texto pequeño debajo: "¿Aún no tienes cuenta? Solicita acceso a
 *   la comisión".
 *
 * Mantiene la lógica del Login anterior (signInWithOtp con
 * shouldCreateUser=false, mensaje específico de rate limit, etc.).
 * Solo cambia la presentación visual al lenguaje del handoff dark.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

type EstadoEnvio = 'idle' | 'enviando' | 'enviado' | 'error';

export function Login() {
  const [email, setEmail] = useState('');
  const [estado, setEstado] = useState<EstadoEnvio>('idle');
  const [mensajeError, setMensajeError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEstado('enviando');
    setMensajeError(null);

    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      const noRegistrado = /signups? not allowed/i.test(error.message);
      const rateLimited =
        error.status === 429 ||
        /rate.?limit|too many requests/i.test(error.message);
      let mensaje: string;
      if (noRegistrado) {
        mensaje = 'Este correo no está registrado en el portal.';
      } else if (rateLimited) {
        mensaje =
          'Pediste enlaces muy seguido. Espera unos 30-60 minutos antes de intentar de nuevo.';
      } else {
        mensaje =
          'No pudimos enviar el enlace. Intenta de nuevo en unos minutos.';
      }
      setMensajeError(mensaje);
      setEstado('error');
      return;
    }

    setEstado('enviado');
  }

  return (
    <div className="w-full max-w-md flex flex-col items-center">
      <img
        src="/escudo-calbucura-original.png"
        alt="Escudo Calbucura"
        className="h-20 w-auto mb-4"
      />
      <h1 className="text-3xl font-medium text-text-primary tracking-widest mb-1">
        CALBUCURA
      </h1>
      <p className="text-sm text-text-secondary mb-8">Portal de vecinos</p>

      <Card className="w-full">
        <h2 className="text-xl font-medium text-text-primary mb-2">
          Inicia sesión
        </h2>
        <p className="text-sm text-text-secondary mb-6">
          Ingresa tu email y te enviaremos un enlace mágico para acceder. No
          necesitas contraseña.
        </p>

        {estado === 'enviado' ? (
          <div className="bg-state-success/15 border-l-4 border-state-success p-4 rounded-sm">
            <p className="text-sm text-text-primary font-medium">
              Enlace enviado a <span className="font-mono text-orange">{email}</span>.
            </p>
            <p className="text-sm text-text-secondary mt-2">
              Revisa tu correo (incluyendo spam). El enlace expira en 1 hora.
            </p>
            <button
              type="button"
              onClick={() => {
                setEstado('idle');
                setEmail('');
              }}
              className="mt-4 text-sm text-orange hover:text-orange-light"
            >
              Enviar a otro correo
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-text-primary mb-1"
              >
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={estado === 'enviando'}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-orange focus:ring-1 focus:ring-orange/30 placeholder:text-text-muted disabled:opacity-50"
                placeholder="tu@correo.cl"
              />
            </div>

            {mensajeError && (
              <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
                <p>{mensajeError}</p>
                {(mensajeError.includes('no está registrado') ||
                  /signups? not allowed/i.test(mensajeError)) && (
                  <p className="mt-2">
                    Si quieres acceso al portal,{' '}
                    <Link
                      to="/solicitar-acceso"
                      className="text-orange hover:underline"
                    >
                      completa el formulario público de solicitud
                    </Link>
                    .
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={estado === 'enviando' || !email}
            >
              {estado === 'enviando' ? 'Enviando…' : 'Enviarme enlace'}
            </Button>
          </form>
        )}
      </Card>

      <p className="text-sm text-text-secondary mt-6 text-center">
        ¿Aún no tienes cuenta?{' '}
        <Link
          to="/solicitar-acceso"
          className="text-orange hover:underline font-medium"
        >
          Solicita acceso a la comisión
        </Link>
      </p>
    </div>
  );
}
